import React from 'react'
import { Skeleton } from '@material-ui/lab'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import { makeStyles } from '@material-ui/core'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { postNamedQuery } from '../../../../../service'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    gap: 'var(--gap-sm)'
  },
  account: {
    display: 'flex',
    padding: 'var(--padding-sm)',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxHeight: '120px',
    minHeight: 'fit-content',
    borderRadius: 'var(--border-radius-lg)'
  }
})

export const AccountList = ({
  entityProps,
  accountCategory
}) => {
  const { clientId, availableDates } = useAppContext()
  const { formatter } = useFormattingContext()

  const classes = useStyles()

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.entityGraphs, clientId, entityProps.levelTypeId, entityProps.levelId, 'accounts'],
    queryFn: async () => {
      const response = await postNamedQuery('coreData', 'getGroupedCoreData', {
        levelFilters: {
          calcType: 'balance',
          dateType: 'all',
          clientIds: [clientId],
          levelTypes: ['client', 'accountCategory', 'account'],
          accountCategoryIds: [entityProps.levelId],
          V2: true
        },
        dateRange: {
          startDate: availableDates.min,
          endDate: availableDates.max
        }
      })
      return response?.data || []
    }
  })

  if (isLoading) {
    return (
      <>
        <hr className='w-full' />
        <div className={classes.accountListContainer}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} width='100%' height='38px' />
          ))}
        </div>
      </>
    )
  }

  if (!data?.length) {
    return (
      <>
        <hr className='w-full' />
        <p className='m-0 font-size-sm'>No Accounts in this Category</p>
      </>
    )
  }

  return (
    <>
      <hr className='w-full' />
      <p className='m-0 font-size-sm'>
        {data?.length} Account{data?.length !== 1 ? 's' : ''} in this Category
      </p>
      <div className={classes.container}>
        {data?.map((account, i) => (
          <div
            key={account.accountId}
            className={classes.account}
            style={{
              backgroundColor: `color-mix(in srgb, ${entityProps?.color ?? '#000'} ${Math.max((100 - ((i + 1) * 12)), 20)}%, transparent)`,
              flexBasis: `${(account?.endingValue / accountCategory?.endingValue) * 100}%`
            }}
          >
            <span className='font-size-sm'>{account.accountName}</span>
            <span className='font-size-md'>{formatter(account?.endingValue ?? 0, '$,0[.]0A')}</span>
          </div>
        ))}
      </div>
    </>
  )
}

AccountList.propTypes = {
  entityProps: PropTypes.shape({
    levelTypeId: PropTypes.number.isRequired,
    levelId: PropTypes.number.isRequired,
    color: PropTypes.string
  }),
  accountCategory: PropTypes.shape({
    endingValue: PropTypes.number
  })
}
